import type { Ad } from "@models/Ad.model";
import { ContentCatalogFolder } from "@models/contentCatalogFolder.model";
import type { TagGroup } from "@models/tagGroup.model";
import { fuzzyMatch } from "@/shared/utils/fuzzyMatch";

/*
    Tag groups the store's filter bar does not offer (Rachel, 30 Aug).

    A hide-list rather than a list of what to show, so a tag group added to the Content
    Catalog later turns up here on its own -- which is what the ticket asks for. Only the
    deliberate omissions are named.

    Size went because it is not how anyone shops for a GuidePost. Customization Level and
    Date are omitted for the same reason and because Rachel did not ask for them; they became
    reachable only when the bar started reading the whole taxonomy, which it has to do to
    show Target Action at all.
*/
const HIDDEN_GROUPS = new Set(["size", "customization level", "date"]);

/** The taxonomy the store offers: everything except the groups above. */
export function storeFilterGroups(groups: TagGroup[]): TagGroup[] {
    return groups.filter((g) => !HIDDEN_GROUPS.has((g.name ?? "").trim().toLowerCase()));
}

/** ad_id -> the tag ids it currently carries. Untagged ads are absent, not empty. */
export type AdTagMap = Record<number, number[]>;

/**
 * Narrowing a Moti Store collection page (MPN-1575).
 *
 * The page already holds every ad it can show, so this filters what is in hand rather than
 * asking the server again. The tag half deliberately matches the Content Catalog: an ad is a
 * hit if it carries ANY selected tag, across groups and within one, which is what the
 * catalog's filter does today.
 *
 * The query half is AND'd with the tags rather than OR'd. The catalog ORs them, because there
 * a query and a tag are two ways of finding content in a library. Here both are narrowing one
 * page, and OR would mean typing a letter ADDS ads that the selected tag excluded.
 */
export function adMatches(
    ad: Ad,
    selectedTagIds: number[],
    query: string,
    adTags: AdTagMap,
): boolean {
    if (selectedTagIds.length > 0) {
        const tags = adTags[ad.id] ?? [];
        if (!tags.some((id) => selectedTagIds.includes(id))) return false;
    }
    if (query.trim()) {
        // `Ad ${id}` mirrors the label the catalog's tree search falls back to, so an unnamed
        // ad is findable by the number the card shows rather than being unreachable.
        if (!fuzzyMatch(ad.name || `Ad ${ad.id}`, query)) return false;
    }
    return true;
}

export function isFiltering(selectedTagIds: number[], query: string): boolean {
    return selectedTagIds.length > 0 || query.trim().length > 0;
}

/**
 * The surviving ads of each folder, with folders that keep nothing dropped entirely.
 *
 * Empty folders are pruned rather than shown empty: a collection can be a dozen accordions
 * deep, and leaving them all on screen with nothing inside hides the handful that matched.
 * A folder stays if it keeps an ad OR if a descendant did.
 */
export function filterFolders(
    folders: ContentCatalogFolder[],
    selectedTagIds: number[],
    query: string,
    adTags: AdTagMap,
): ContentCatalogFolder[] {
    if (!isFiltering(selectedTagIds, query)) return folders;

    const prune = (folder: ContentCatalogFolder): ContentCatalogFolder | null => {
        const ads = (folder.ads ?? []).filter((ad) => adMatches(ad, selectedTagIds, query, adTags));
        const children = (folder.children ?? [])
            .map(prune)
            .filter((child): child is ContentCatalogFolder => child !== null);

        if (ads.length === 0 && children.length === 0) return null;

        // A copy, so clearing the filter restores the original page rather than the last
        // narrowed version of it.
        const kept = new ContentCatalogFolder();
        Object.assign(kept, folder, { ads, children });
        return kept;
    };

    return folders
        .map(prune)
        .filter((folder): folder is ContentCatalogFolder => folder !== null);
}

export function filterAds(
    ads: Ad[],
    selectedTagIds: number[],
    query: string,
    adTags: AdTagMap,
): Ad[] {
    if (!isFiltering(selectedTagIds, query)) return ads;
    return ads.filter((ad) => adMatches(ad, selectedTagIds, query, adTags));
}
